import React, { useState } from 'react';
import { Clock, ChefHat, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RecipeCard = ({
  image,
  title,
  description,
  cookTime,
  difficulty = 'Medium',
  rating = 0,
  tags = [],
  onClick,
  actionsComponent,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isOptionsHovered, setIsOptionsHovered] = useState(false);

  const getDifficultyColor = (level) => {
    const colors = {
      Easy: 'text-green-600 bg-green-50',
      Medium: 'text-yellow-600 bg-yellow-50',
      Hard: 'text-red-600 bg-red-50'
    };
    return colors[level] || colors.Medium;
  };

  const translateDifficulty = (level) => {
    return t(`recipe.difficultyLevels.${level}`, { defaultValue: level });
  };

  return (
    <div
      className={`group relative bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden cursor-pointer transform ${isOptionsHovered ? '' : 'hover:shadow-2xl hover:-translate-y-2'} ${className}`}
      onClick={onClick}
    >
      {/* Image container */}
      <div className="relative h-56 overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
        {image ? (
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ChefHat className="w-20 h-20 text-primary-300" />
          </div>
        )}



        {/* Difficulty badge */}
        <div className="absolute top-4 left-4">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${getDifficultyColor(
              difficulty
            )}`}
          >
            {translateDifficulty(difficulty)}
          </span>
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Actions component - movido fuera del contenedor de imagen para evitar problemas de z-index */}
      {actionsComponent && (
        <div
          className="absolute top-4 right-4 z-[100]"
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseEnter={() => setIsOptionsHovered(true)}
          onMouseLeave={() => setIsOptionsHovered(false)}
        >
          {actionsComponent}
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        {/* Title */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {description}
          </p>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg"
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer info */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          {/* Cook time */}
          {cookTime && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{cookTime}</span>
            </div>
          )}

          {/* Rating */}
          {rating > 0 && (
            <div className="flex items-center gap-1.5 text-yellow-500">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium text-gray-700">{rating.toFixed(1)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Hover effect border */}
      <div className="absolute inset-0 border-2 border-primary-500 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
};

export default RecipeCard;